import React from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AdminAssistGroundedSuggestion from './AdminAssistGroundedSuggestion';
import AdminAssistInvestorResponses from './AdminAssistInvestorResponses';
import {
  ChatAiAssistFollowUpComposer,
  ChatAiAssistFollowUpItem,
} from './ChatAiAssistFollowUpSection';
import { truncateMessagePreview } from './chatAiAssistDrawerUtils';
import { ChatAiAssistState } from '../../hooks/admin/useChatAiAssist';
import { AdminChatAssistResult } from '../../models/admin/AdminChatAssistResult';
import { ChatLinkTarget } from '../../utils/chatLinkCorpus';

interface ChatAiAssistHistoryEntryProps {
  entry: AdminChatAssistResult;
  expanded: boolean;
  isLastEntry: boolean;
  latestEntryRef?: React.RefObject<HTMLDivElement | null>;
  assist: ChatAiAssistState;
  conversationId: string | null;
  corpus: ChatLinkTarget[] | null;
  onUseInvestorResponse?: (text: string) => void;
}

export const ChatAiAssistHistoryEntry: React.FC<ChatAiAssistHistoryEntryProps> = ({
  entry,
  expanded,
  isLastEntry,
  latestEntryRef,
  assist,
  conversationId,
  corpus,
  onUseInvestorResponse,
}) => {
  const previewShort = truncateMessagePreview(entry.targetMessageText);

  return (
    <Box
      ref={isLastEntry ? latestEntryRef : undefined}
      sx={{
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: expanded ? '#c5d4f7' : '#e3e8ef',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          px: 1,
          py: 0.75,
          cursor: 'pointer',
          bgcolor: expanded ? '#f3f7ff' : 'transparent',
          '&:hover': { bgcolor: expanded ? '#f3f7ff' : '#fafbfc' },
        }}
        onClick={() => assist.setExpandedTargetId(expanded ? null : entry.targetMessageId)}
      >
        <IconButton
          size="small"
          sx={{ mt: -0.25, p: 0.25 }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: 'block' }}
          >
            {entry.targetMessageSenderName}
            {entry.intentResult && (
              <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.85 }}>
                · {entry.intentResult}
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }} noWrap={!expanded}>
            {expanded ? entry.targetMessageText : previewShort}
          </Typography>
        </Box>
        {entry.cached && (
          <Chip size="small" label="Saved" variant="outlined" sx={{ height: 20, mt: 0.25 }} />
        )}
      </Box>

      {expanded && (
        <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5, borderTop: '1px solid #eef2f7' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <Tooltip title="Regenerate Vera assist for this message (clears follow-ups)">
              <span>
                <IconButton
                  size="small"
                  aria-label="Regenerate Vera assist"
                  onClick={(event) => {
                    event.stopPropagation();
                    void assist.loadAssist(true, entry.targetMessageId);
                  }}
                  disabled={!conversationId || assist.loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <AdminAssistGroundedSuggestion
            messageId={`ai-assist-${conversationId ?? 'none'}-${entry.targetMessageId}`}
            text={entry.suggestion}
            corpus={corpus}
            grounding={entry.googleSearchGrounding}
            markdownVariant="block"
          />
          {onUseInvestorResponse && (
            <AdminAssistInvestorResponses
              responses={entry.investorResponses ?? []}
              onSelect={onUseInvestorResponse}
            />
          )}

          {(entry.followUps ?? []).map((followUp) => (
            <ChatAiAssistFollowUpItem
              key={followUp.id}
              followUp={followUp}
              conversationId={conversationId}
              corpus={corpus}
              targetMessageId={entry.targetMessageId}
              assist={assist}
              onUseInvestorResponse={onUseInvestorResponse}
            />
          ))}

          <ChatAiAssistFollowUpComposer targetMessageId={entry.targetMessageId} assist={assist} />
        </Box>
      )}
    </Box>
  );
};
